'use client';

import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ISocialPost } from 'types/src/types';
import { AuthorBlock } from './AuthorBlock';
import { PostActions } from './PostActions';
import { GifBlock } from './GifBlock';
import { YouTubeBlock } from './YouTubeBlock';
import {
  extractYouTubeId,
  getYouTubeVideoInfoViaServer,
} from '@/src/utils/youtube';
import { YouTubeModal } from '../../profile/components';
import { ImagesGallery } from '@/src/components/ui/ImagesGallery';
import { IUIProfile } from '@/src/types/IUIProfile';
import { Icon } from '@/src/components/ui';
import { DotsButton } from './DotsButton';
import { cn } from '@workspace/ui/lib/utils';
import { Modal } from './Modal';
import { ReplyForm } from './comments/ReplyForm';
import { useBoolean } from '@/src/hooks/use-boolean';

type Props = {
  post: ISocialPost;
  profile?: IUIProfile;
  repostedBy?: string;
  isMobile?: boolean;
  toggleReaction?: any;
  isPostPage?: boolean;
};

export const PostItem = ({
  post,
  profile,
  repostedBy,
  isMobile,
  toggleReaction,
  isPostPage,
}: Props) => {
  const {
    gifId,
    author,
    createdAt,
    linkedGame,
    text,
    youTubeVideoUrl,
    uploadedVideoUrl,
    uploadedImagesUrl,
  } = post;

  const [isYouTubeOpen, setIsYouTubeOpen] = useState(false);
  const [youTube, setYouTube] = useState({
    title: '',
    description: '',
    thumbnail: '',
  });

  const hasImages = uploadedImagesUrl && uploadedImagesUrl.length > 0;
  const videoId = youTubeVideoUrl
    ? (extractYouTubeId(youTubeVideoUrl) ?? '')
    : '';

  const youtubeThumb = videoId
    ? `https://i.ytimg.com/vi/${encodeURIComponent(videoId)}/hqdefault.jpg`
    : '';

  const isAnonymous = profile?.type === 'anonymous';
  const isMyPost = !isAnonymous && profile?.id === author.id;
  const router = useRouter();
  const searchParams = useSearchParams();
  const isOpenReplyModal = useBoolean(false);
  const isGif = uploadedVideoUrl?.toLowerCase().endsWith('.gif');
  const [mediaAspect, setMediaAspect] = useState<number | null>(null);

  const onGameClick = (slug: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('game', slug);
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  useEffect(() => {
    const loadInfo = async () => {
      if (!videoId) return;

      try {
        const info = await getYouTubeVideoInfoViaServer(videoId);

        if (info) {
          if (info.title) {
            setYouTube((prev) => ({ ...prev, title: info.title || '' }));
          }
          if (info.description) {
            setYouTube((prev) => ({
              ...prev,
              description: info.description || '',
            }));
          }
        }
      } catch (e) {
        console.error('Failed to load YouTube info for feed item', e);
      }
    };

    loadInfo();
  }, [videoId]);

  const openPost = () => {
    router.push(`/${post.author.uniqueLink}/social-post/${post.id}`);
  };

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    if (target.closest('[data-no-card-click="true"]')) return;
    if (target.closest('a, button, input, textarea, select')) return;

    openPost();
  };

  const cardDisabled = isOpenReplyModal.value || isPostPage;

  return (
    <div className="md:mt-8 mt-5 px-6 md:px-0">
      {repostedBy && (
        <div className="text-sm text-blue-gray md:ml-18 mb-1 font-semibold flex items-center gap-1">
          <Icon name="post-repost" className="size-4" />
          Reposted by @{repostedBy}
        </div>
      )}
      <div
        className={cn('flex md:gap-4 gap-2.5 md:mx-3', {
          'hover:bg-blue-primary/5 rounded-lg p-1 box-content transition-colors duration-200 hover:cursor-pointer':
            !isPostPage,
        })}
        onClick={!cardDisabled ? handleCardClick : undefined}
        role={!cardDisabled ? 'button' : undefined}
        tabIndex={!cardDisabled ? 0 : undefined}
      >
        <a
          data-no-card-click="true"
          href={`/${author.uniqueLink}`}
          className="size-[42px] rounded-full hover:border-blue-primary box-content border-1 border-transparent transition-colors duration-400"
        >
          <Image
            src={author.avatarUrl || '/images/default-avatar.png'}
            alt={author.name || 'User Avatar'}
            width={42}
            height={42}
          />
        </a>
        <div className="w-full flex flex-col max-w-[570px]">
          <div className="flex justify-between items-start">
            <AuthorBlock
              name={author.fullName || author.name || 'Unknown'}
              uniqueLink={author.uniqueLink}
              level={author.level as number}
              createdAt={createdAt}
            />
            {isMobile && <DotsButton post={post} isMyPost={isMyPost} />}
          </div>
          {!!linkedGame?.slug && (
            <button
              data-no-card-click="true"
              type="button"
              onClick={() => onGameClick(linkedGame.slug as string)}
              className="flex gap-1 text-bluesky-gray md:mt-4 mt-2 items-center hover:underline hover:cursor-pointer md:text-base text-sm"
            >
              <Image
                src={linkedGame?.coverImageUrl || ''}
                alt="Project Icon"
                className="size-5"
                width={20}
                height={20}
              />
              <span className="text-start">{linkedGame?.title || ''}</span>
            </button>
          )}
          <div
            className="post-content mt-4 text-sm md:text-base"
            style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: text }}
          />

          {gifId && <GifBlock gifId={gifId} />}
          {youTubeVideoUrl && youtubeThumb && videoId && (
            <div className="md:mt-2" data-no-card-click="true">
              <button
                type="button"
                className="block w-full text-left p-0 m-0 bg-transparent border-0 leading-none hover:cursor-pointer"
                onClick={() => setIsYouTubeOpen(true)}
              >
                <YouTubeBlock
                  youtubeThumb={youtubeThumb}
                  title={youTube.title}
                  description={youTube.description}
                />
              </button>

              {isYouTubeOpen && (
                <YouTubeModal
                  isOpen
                  onClose={() => setIsYouTubeOpen(false)}
                  videoId={videoId}
                  title="YouTube video"
                />
              )}
            </div>
          )}

          {uploadedVideoUrl && (
            <div className="mt-2 flex justify-start">
              <div
                data-no-card-click="true"
                className="bg-black/5 rounded-md overflow-hidden max-h-[300px]"
                style={{
                  aspectRatio: mediaAspect ? String(mediaAspect) : undefined,
                }}
              >
                {!isGif ? (
                  <video
                    data-no-card-click="true"
                    controls
                    playsInline
                    controlsList="nodownload"
                    preload="metadata"
                    src={uploadedVideoUrl}
                    className="w-full h-full object-contain"
                    onLoadedMetadata={(e) => {
                      const v = e.currentTarget;
                      if (v.videoWidth && v.videoHeight) {
                        setMediaAspect(v.videoWidth / v.videoHeight);
                      }
                    }}
                  />
                ) : (
                  <img
                    data-no-card-click="true"
                    src={uploadedVideoUrl}
                    alt="Uploaded gif"
                    className="w-full h-full object-contain"
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      if (img.naturalWidth && img.naturalHeight) {
                        setMediaAspect(img.naturalWidth / img.naturalHeight);
                      }
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {hasImages && (
            <div className="mt-2">
              <ImagesGallery
                isOne={false}
                imageSize={140}
                urls={
                  uploadedImagesUrl.length >= 1 ? uploadedImagesUrl : undefined
                }
              />
            </div>
          )}
          <>
            <PostActions
              isMyPost={isMyPost}
              post={post}
              isAnonymous={isAnonymous}
              isMobile={isMobile}
              toggleReaction={toggleReaction}
              isOpenReplyFromFeed={!isPostPage ? isOpenReplyModal : undefined}
            />
          </>
        </div>
      </div>
      {!isPostPage && (
        <>
          <hr className="md:mt-6 mt-2 border-t border-blue-gray-light" />

          <Modal open={isOpenReplyModal.value} isPost={false}>
            <div
              data-no-card-click="true"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onKeyDownCapture={(e) => e.stopPropagation()}
            >
              <ReplyForm
                handleCloseModal={isOpenReplyModal.onFalse}
                parentCommentId={null}
                replyToComment={null}
                postId={post.id}
                onCreated={(newId) => {
                  router.push(
                    `/${post.author.uniqueLink}/social-post/${post.id}?commentId=${newId}`,
                  );
                }}
              />
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};
